import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Facebook, Youtube, Instagram, Twitter } from "lucide-react";
import { SITE, TEMPLE_TIMINGS, OFFICIAL } from "../../data/site";
import { YOUTUBE_CHANNEL_URL } from "../../data/youtube";
import Mandala from "../decor/Mandala";
import { useLang } from "../../i18n/LanguageContext";

const periodKey = (period) => {
  if (period === "Morning Darshana") return "footer.morning";
  if (period === "Afternoon Break") return "footer.afternoon";
  if (period === "Evening Darshana") return "footer.evening";
  return null;
};

const GooglePlayIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
    <path fill="#34A853" d="M4.5 2.4c-.3.3-.5.8-.5 1.4v16.4c0 .6.2 1.1.5 1.4l8.7-9.6L4.5 2.4Z" />
    <path fill="#FBBC04" d="m16.2 8.5-3 3.5 3 3.5 3.9-2.2c1.2-.7 1.2-1.9 0-2.6l-3.9-2.2Z" />
    <path fill="#4285F4" d="M4.5 2.4 13.2 12l3-3.5L6.1 2.7c-.6-.4-1.2-.5-1.6-.3Z" />
    <path fill="#EA4335" d="m4.5 21.6c.4.2 1 .1 1.6-.3l10.1-5.8-3-3.5-8.7 9.6Z" />
  </svg>
);

const AppStoreIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
    <rect width="24" height="24" rx="5.5" fill="#0A84FF" />
    <path d="M8.1 16.9h7.8" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M10.1 14.3 13 9.2" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" />
    <path d="m11.8 6.9.8 1.4" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" />
    <path d="m13.9 14.3-3-5.1" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" />
    <path d="m9 18.2-.9 1.5" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" />
    <path d="m15 18.2.9 1.5" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const socialLinks = [
  { label: "Facebook", href: "https://www.facebook.com/SodeMatha/", Icon: Facebook },
  { label: "YouTube", href: YOUTUBE_CHANNEL_URL, Icon: Youtube },
  { label: "Instagram", href: "https://www.instagram.com/sodematha/?hl=en", Icon: Instagram },
  { label: "X", href: "https://twitter.com/sodematha?lang=en", Icon: Twitter },
];

const Footer = () => {
  const { t, lang } = useLang();
  const brandTop = lang === "kn" ? "ಶ್ರೀ ಸೋದೆ" : "SRI SODE";
  const brandBottom = lang === "kn" ? "ವಾದಿರಾಜ ಮಠ" : "Vadiraja Matha";
  const address = lang === "kn" ? "ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠ, ಸೋಂದಾ, ಸಿರಸಿ ತಾಲೂಕು" : SITE.address;
  return (
    <footer className="relative overflow-hidden bg-[#B54A00] text-[#F2E7D5]" data-testid="site-footer">
      {/* silhouette watermark */}
      <div className="absolute right-[-120px] top-[-120px] pointer-events-none">
        <Mandala size={480} color="#FBF7F0" opacity={0.08} />
      </div>
      <div className="absolute left-[-100px] bottom-[-100px] pointer-events-none">
        <Mandala size={360} color="#FBF7F0" opacity={0.07} />
      </div>

      {/* Temple silhouette */}
      <svg viewBox="0 0 1200 80" className="w-full -mb-1 text-[#B54A00]" preserveAspectRatio="none" style={{ height: 40 }}>
        <path
          fill="currentColor"
          d="M0 80 L0 40 L60 40 L60 30 L90 10 L120 30 L120 40 L200 40 L200 20 L230 0 L260 20 L260 40 L340 40 L340 25 L380 5 L420 25 L420 40 L560 40 L560 15 L600 -5 L640 15 L640 40 L780 40 L780 25 L820 5 L860 25 L860 40 L940 40 L940 20 L970 0 L1000 20 L1000 40 L1080 40 L1080 30 L1110 10 L1140 30 L1140 40 L1200 40 L1200 80 Z"
        />
      </svg>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-16 pb-10 relative">
        <div className="grid md:grid-cols-4 gap-10">
          {/* Column 1 */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-full bg-[#FBF7F0]/95 flex items-center justify-center p-1">
                <img src={OFFICIAL.logo} alt="Sri Sode Vadiraja Matha" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="font-cinzel text-sm tracking-widest text-[#FBF7F0]">{brandTop}</div>
                <div className="font-serif-display text-lg">{brandBottom}</div>
              </div>
            </div>
            <p className="text-sm text-[#FBF7F0]/90 leading-relaxed">
              {t("footer.legacyDesc")}
            </p>
            <div className="flex items-center gap-3 mt-5">
              {socialLinks.map(({ label, href, Icon }, i) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="w-9 h-9 rounded-full border border-[#FBF7F0]/50 flex items-center justify-center hover:bg-[#FBF7F0] hover:text-[#B54A00] transition"
                  data-testid={`social-${i}`}
                  aria-label={lang === "kn" ? `${label} ಸಾಮಾಜಿಕ ಜಾಲತಾಣ ಲಿಂಕ್` : `${label} social link`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2 */}
          <div>
            <h4 className="font-cinzel text-[#FBF7F0] text-sm tracking-widest mb-4">{t("footer.explore")}</h4>
            <ul className="space-y-2 text-sm">
              {[
                [t("footer.aboutSode"), "/about"],
                [t("footer.vadiraja"), "/lineage?tab=vadiraja"],
                [t("footer.parampara"), "/lineage?tab=guru"],
                [t("footer.deities"), "/deities"],
                [t("footer.festivals"), "/events"],
                [t("footer.publications"), "/publications"],
                [t("footer.gallery"), "/gallery"],
              ].map(([l, to]) => (
                <li key={to}>
                  <Link to={to} className="hover:text-[#3B2A1A] transition" data-testid={`footer-link-${to.replace("/", "")}`}>{l}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 */}
          <div>
            <h4 className="font-cinzel text-[#FBF7F0] text-sm tracking-widest mb-4">{t("footer.timings")}</h4>
            <ul className="space-y-3 text-sm">
              {TEMPLE_TIMINGS.map((ti) => {
                const k = periodKey(ti.period);
                return (
                  <li key={ti.period}>
                    <div className="flex items-center gap-2 text-[#FBF7F0]"><Clock className="w-3.5 h-3.5 text-[#FBF7F0]" /> {k ? t(k) : ti.period}</div>
                    <div className="text-[#FBF7F0]/80 text-xs ml-5.5 pl-5">{lang === "kn" ? ti.timeKn : ti.time}</div>
                  </li>
                );
              })}
            </ul>
            <div className="mt-7">
              <div className="font-cinzel text-[11px] tracking-widest text-[#FBF7F0]/85 mb-3">
                {lang === "kn" ? "ಅಪ್ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ" : "Download the app"}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <a
                  href="#"
                  className="inline-flex min-w-0 items-center gap-2 rounded-xl border border-[#FBF7F0]/45 bg-[#3B2A1A]/20 px-3 py-2.5 text-[#FBF7F0] hover:bg-[#FBF7F0] hover:text-[#B54A00] transition"
                  data-testid="footer-google-play"
                  aria-label={lang === "kn" ? "ಗೂಗಲ್ ಪ್ಲೇನಲ್ಲಿ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ" : "Download on Google Play"}
                >
                  <GooglePlayIcon className="w-5 h-5 shrink-0" />
                  <span className="min-w-0 leading-tight">
                    <span className="block text-[9px] uppercase tracking-wider opacity-80">{lang === "kn" ? "ಪಡೆಯಿರಿ" : "Get it on"}</span>
                    <span className="block text-xs font-semibold">Google Play</span>
                  </span>
                </a>
                <a
                  href="#"
                  className="inline-flex min-w-0 items-center gap-2 rounded-xl border border-[#FBF7F0]/45 bg-[#3B2A1A]/20 px-3 py-2.5 text-[#FBF7F0] hover:bg-[#FBF7F0] hover:text-[#B54A00] transition"
                  data-testid="footer-app-store"
                  aria-label={lang === "kn" ? "ಆಪ್ ಸ್ಟೋರ್‌ನಲ್ಲಿ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ" : "Download on the App Store"}
                >
                  <AppStoreIcon className="w-5 h-5 shrink-0" />
                  <span className="min-w-0 leading-tight">
                    <span className="block text-[9px] uppercase tracking-wider opacity-80">{lang === "kn" ? "ಡೌನ್‌ಲೋಡ್" : "Download"}</span>
                    <span className="block text-xs font-semibold">App Store</span>
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* Column 4 */}
          <div>
            <h4 className="font-cinzel text-[#FBF7F0] text-sm tracking-widest mb-4">{t("footer.contact")}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#FBF7F0] shrink-0" /> {address}</li>
              <li><a href={`tel:${SITE.phone}`} className="flex gap-2 hover:text-[#3B2A1A] transition"><Phone className="w-4 h-4 mt-0.5 text-[#FBF7F0]" /> {SITE.phone}</a></li>
              <li><a href={`mailto:${SITE.email}`} className="flex gap-2 hover:text-[#3B2A1A] transition"><Mail className="w-4 h-4 mt-0.5 text-[#FBF7F0]" /> {SITE.email}</a></li>
            </ul>
            <Link to="/sevas" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#FBF7F0] text-[#B54A00] px-5 py-2.5 text-sm font-semibold hover:bg-[#3B2A1A] hover:text-[#FBF7F0] transition" data-testid="footer-donate-cta">
              {t("footer.support")}
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-[#FBF7F0]/30 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#FBF7F0]/80">
          <p>© {new Date().getFullYear()} {t("site.name")} • {t("footer.rights")}</p>
          <p className="italic font-serif-display text-sm text-[#FBF7F0]">॥ श्री हयग्रीवाय नमः ॥</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
